import { useState, useEffect } from "react"
import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Checkbox,
  RadioGroup,
  Radio,
  Stack,
} from "@mui/material"
import { Control, Controller, useWatch } from "react-hook-form"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs from "dayjs"

export type RecurrenceFrequency = "daily" | "weekly" | "monthly"
export type RecurrenceEndType = "date" | "count" | "none"

export interface RecurrenceValues {
  enabled: boolean
  frequency: RecurrenceFrequency
  interval: number
  daysOfWeek: number[]
  endType: RecurrenceEndType
  endDate?: Date
  endCount?: number
}

interface RecurrenceSelectorProps {
  control: Control<any>
  name: string
  startDate?: Date
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
]

/**
 * Calculate the number of occurrences that will be generated
 */
function calculateOccurrenceCount(
  startDate: Date,
  values: RecurrenceValues
): number {
  if (!values.enabled) return 1

  const maxMonths = 3
  const maxOccurrences = 52

  let endDate: Date
  let maxCount: number

  switch (values.endType) {
    case "date":
      endDate = values.endDate || addMonths(startDate, maxMonths)
      maxCount = maxOccurrences
      break
    case "count":
      endDate = addMonths(startDate, 12)
      maxCount = Math.min(values.endCount || maxOccurrences, maxOccurrences)
      break
    case "none":
    default:
      endDate = addMonths(startDate, maxMonths)
      maxCount = maxOccurrences
      break
  }

  let count = 0
  let currentDate = new Date(startDate)

  while (currentDate <= endDate && count < maxCount) {
    if (values.frequency === "weekly" && values.daysOfWeek?.length) {
      if (values.daysOfWeek.includes(currentDate.getDay())) {
        count++
      }
      currentDate = addDays(currentDate, 1)
    } else {
      count++
      switch (values.frequency) {
        case "daily":
          currentDate = addDays(currentDate, values.interval)
          break
        case "weekly":
          currentDate = addDays(currentDate, 7 * values.interval)
          break
        case "monthly":
          currentDate = addMonths(currentDate, values.interval)
          break
      }
    }
  }

  return count
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

export default function RecurrenceSelector({
  control,
  name,
  startDate,
}: RecurrenceSelectorProps) {
  const recurrenceValues = useWatch({ control, name })
  const watchedStartDate = useWatch({ control, name: "event_start_timestamp" })
  const effectiveStartDate = startDate || watchedStartDate || new Date()

  const [occurrenceCount, setOccurrenceCount] = useState(1)

  useEffect(() => {
    if (recurrenceValues) {
      const count = calculateOccurrenceCount(effectiveStartDate, recurrenceValues)
      setOccurrenceCount(count)
    }
  }, [recurrenceValues, effectiveStartDate])

  const getFrequencyLabel = (frequency: RecurrenceFrequency) => {
    switch (frequency) {
      case "daily":
        return "day(s)"
      case "weekly":
        return "week(s)"
      case "monthly":
        return "month(s)"
    }
  }

  return (
    <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 2 }}>
      <Controller
        name={`${name}.enabled`}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={field.value || false}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label="Recurring Event"
          />
        )}
      />

      {recurrenceValues?.enabled && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {/* Frequency Selection */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography>Every</Typography>
            <Controller
              name={`${name}.interval`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ min: 1, max: 12 }}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
            <Controller
              name={`${name}.frequency`}
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select {...field}>
                    <MenuItem value="daily">Day(s)</MenuItem>
                    <MenuItem value="weekly">Week(s)</MenuItem>
                    <MenuItem value="monthly">Month(s)</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Stack>

          {/* Days of Week (for weekly) */}
          {recurrenceValues?.frequency === "weekly" && (
            <Controller
              name={`${name}.daysOfWeek`}
              control={control}
              render={({ field }) => (
                <FormControl component="fieldset">
                  <FormLabel component="legend">Repeat on</FormLabel>
                  <FormGroup row>
                    {DAYS_OF_WEEK.map((day) => (
                      <FormControlLabel
                        key={day.value}
                        control={
                          <Checkbox
                            checked={(field.value || []).includes(day.value)}
                            onChange={(e) => {
                              const currentDays = field.value || []
                              if (e.target.checked) {
                                field.onChange([...currentDays, day.value])
                              } else {
                                field.onChange(
                                  currentDays.filter(
                                    (d: number) => d !== day.value
                                  )
                                )
                              }
                            }}
                          />
                        }
                        label={day.label}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              )}
            />
          )}

          {/* End Condition */}
          <Controller
            name={`${name}.endType`}
            control={control}
            render={({ field }) => (
              <FormControl component="fieldset">
                <FormLabel component="legend">Ends</FormLabel>
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value="none"
                    control={<Radio />}
                    label="After 3 months"
                  />
                  <FormControlLabel
                    value="date"
                    control={<Radio />}
                    label="On date"
                  />
                  <FormControlLabel
                    value="count"
                    control={<Radio />}
                    label="After occurrences"
                  />
                </RadioGroup>
              </FormControl>
            )}
          />

          {/* End Date Picker */}
          {recurrenceValues?.endType === "date" && (
            <Controller
              name={`${name}.endDate`}
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="End Date"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) => field.onChange(date?.toDate())}
                  minDate={dayjs(effectiveStartDate)}
                  slotProps={{
                    textField: { size: "small", sx: { maxWidth: 200 } },
                  }}
                />
              )}
            />
          )}

          {/* End Count */}
          {recurrenceValues?.endType === "count" && (
            <Controller
              name={`${name}.endCount`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Number of occurrences"
                  type="number"
                  size="small"
                  sx={{ maxWidth: 200 }}
                  inputProps={{ min: 1, max: 52 }}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
          )}

          {/* Preview */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will create{" "}
            <strong>{occurrenceCount} event{occurrenceCount !== 1 ? "s" : ""}</strong>
          </Typography>
        </Stack>
      )}
    </Box>
  )
}
